import { Fragment, useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { JSONContent } from '@tiptap/core';

import type { LessonActivitiesFragment } from '../lesson-builder.graphql.generated';
import { SectionOrSubSectionOverview } from 'approot/activity-builder/activity-builder.constants';
import { useActivityBuilderContext } from 'approot/activity-builder/activity-builder.context';
import { useUpdateDraftSectionUsingParams } from './activity-builder-draft.hooks';
import { ACTIVITY_BUILDER_SLIDE_NAVIGATION } from 'approot/activity-builder/activity-builder-skip-links';
import { ExtraSectionIdentifiers } from 'approot/activity-builder/activity-builder.apollo';
import { Differentiation } from 'approot/shared/differentiation/differentiation.constants';
import {
  ActivityBuilderDraftSlideItem,
  ActivityBuilderDraftSlidesNav,
  ActivityBuilderDraftSubSlideItem,
  ActivityBuilderDraftSubSlideUl,
} from 'approot/activity-builder/draft/activity-builder-draft-slides.style';
import { useGetInteractiveLessonParams } from 'approot/activity-builder/activity-builder.hooks';
import { getDraftUrl } from 'approot/activity-builder/activity-builder-url.utils';
import { ActivityBuilderIntroPreview } from '../page/activity-builder-page-preview';
import ActivityBuilderPagePreviewDraft from '../page/activity-builder-page-preview-draft';
import * as Styled from 'approot/activity-builder/activity-builder-slides.style';
import type { Pluck } from 'lib/gql/pluck';

const ActivityBuilderDraftSlides = ({
  activeSectionId,
  sortedSectionsOverview,
  sectionsAndSubSectionsOverview,
  isDrawerActive,
  contentId,
  focusQuestion,
  phenomena,
  assessmentName,
  introBackground,
  setActiveSection,
}: {
  activeSectionId: string;
  sortedSectionsOverview: Pluck<LessonActivitiesFragment, 'sectionsOverview'>[];
  sectionsAndSubSectionsOverview: SectionOrSubSectionOverview[];
  contentId: number;
  isDrawerActive: boolean;
  focusQuestion?: Pluck<LessonActivitiesFragment, 'focusQuestion'> | null;
  phenomena?: Pluck<LessonActivitiesFragment, 'focusQuestion'> | null;
  assessmentName?: JSONContent | null;
  introBackground?: string | null;
  setActiveSection: (id: string) => void;
}) => {
  const navigate = useNavigate();
  const { slug, userClassId, draftId, contentType } =
    useGetInteractiveLessonParams();

  const [containerRef, setContainerRef] = useState<HTMLElement | null>(null);
  const isInitialisedRef = useRef<boolean>(false);

  const { clearActiveComponent } = useActivityBuilderContext();

  const handleSelect = useCallback(
    (sectionId: string) => {
      setActiveSection(sectionId);
      navigate(
        getDraftUrl({
          slug,
          userClassId: `${userClassId}`,
          draftId,
          sectionId,
          contentType,
        })
      );
      // make sure to deselect the active component
      clearActiveComponent();
    },
    [
      setActiveSection,
      navigate,
      userClassId,
      slug,
      draftId,
      clearActiveComponent,
      contentType,
    ]
  );

  useUpdateDraftSectionUsingParams({
    sortedSections: sectionsAndSubSectionsOverview,
    isInitialisedRef,
    containerRef,
  });

  return (
    <ActivityBuilderDraftSlidesNav
      id={ACTIVITY_BUILDER_SLIDE_NAVIGATION}
      ref={setContainerRef}
      tabIndex={-1}
      aria-label="Activity Previews"
    >
      <Styled.ActivityBuilderSlideItems>
        <ActivityBuilderDraftSlideItem
          $isActive={ExtraSectionIdentifiers.Introduction === activeSectionId}
        >
          <Styled.ActivityBuilderSlide
            type="button"
            onClick={() => handleSelect(ExtraSectionIdentifiers.Introduction)}
          >
            <Styled.ActivityBuilderSlideIndicator>
              0
            </Styled.ActivityBuilderSlideIndicator>
            <Styled.ActivityBuilderSlideInner>
              <ActivityBuilderIntroPreview
                focusQuestion={focusQuestion}
                phenomena={phenomena}
                introBackground={introBackground}
                assessmentName={assessmentName}
              />
            </Styled.ActivityBuilderSlideInner>
          </Styled.ActivityBuilderSlide>
        </ActivityBuilderDraftSlideItem>
        {sortedSectionsOverview.map((section, i) => {
          const differentiation =
            section.differentiation || Differentiation.GreenCircle;

          return (
            <Fragment key={section.id}>
              <ActivityBuilderDraftSlideItem
                id={`section-${section.id}`}
                $isActive={section.id === activeSectionId}
                $differentiation={differentiation}
              >
                <Styled.ActivityBuilderSlide
                  type="button"
                  onClick={() => handleSelect(sortedSectionsOverview[i].id)}
                  aria-label={`Click to view activity ${i + 1}`}
                  data-selector={`lb-slide-${i + 1}`}
                >
                  <Styled.ActivityBuilderSlideIndicator>
                    {i + 1}
                  </Styled.ActivityBuilderSlideIndicator>
                  <Styled.ActivityBuilderSlideInner>
                    <ActivityBuilderPagePreviewDraft
                      id={sortedSectionsOverview[i].id}
                      contentDraftId={draftId}
                      contentId={contentId}
                      initialVisibility={
                        activeSectionId === sortedSectionsOverview[i].id
                      }
                      parentElement={containerRef}
                    />
                  </Styled.ActivityBuilderSlideInner>
                </Styled.ActivityBuilderSlide>
              </ActivityBuilderDraftSlideItem>

              {section.subSectionsOverview &&
                section.subSectionsOverview.length > 0 && (
                  <ActivityBuilderDraftSubSlideUl>
                    {section.subSectionsOverview?.map(subSection => {
                      return (
                        <ActivityBuilderDraftSubSlideItem
                          id={`section-${subSection.id}`}
                          key={subSection.id}
                          $isActive={subSection.id === activeSectionId}
                          $differentiation={differentiation}
                        >
                          <Styled.ActivityBuilderSlide
                            type="button"
                            onClick={() => handleSelect(subSection.id)}
                            aria-label={`Click to view activity ${
                              i + 1
                            }'s sub-activity`}
                            data-selector={`lb-sub-slide-${i + 1}`}
                          >
                            <Styled.ActivityBuilderSlideInner>
                              <ActivityBuilderPagePreviewDraft
                                id={subSection.id}
                                contentId={contentId}
                                contentDraftId={draftId}
                                initialVisibility={
                                  activeSectionId === subSection.id
                                }
                                parentElement={containerRef}
                              />
                            </Styled.ActivityBuilderSlideInner>
                          </Styled.ActivityBuilderSlide>
                        </ActivityBuilderDraftSubSlideItem>
                      );
                    })}
                  </ActivityBuilderDraftSubSlideUl>
                )}
            </Fragment>
          );
        })}
      </Styled.ActivityBuilderSlideItems>
    </ActivityBuilderDraftSlidesNav>
  );
};

export default ActivityBuilderDraftSlides;
